import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API, { getErrorMessage } from "../api/axios";
import { hasFullAccess, isAdmin, getUser } from "../auth/auth";
import { useToast } from "../components/Toast";
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  TableCell,
  TableRow,
  Button,
  Stack,
  Avatar,
  Select,
  MenuItem,
} from "@mui/material";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ACCENT, NAVY } from "../theme";
import DataTable from "../components/ui/DataTable";
import EmptyState from "../components/ui/EmptyState";
import LoadingState from "../components/ui/LoadingState";
import StatusBadge from "../components/ui/StatusBadge";
import SearchInput from "../components/ui/SearchInput";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import PageContainer from "../components/ui/PageContainer";
import FilterChipBar from "../components/ui/FilterChipBar";

// Same color-to-tone mapping used in Phases 3–6: maps this file's existing
// MUI Chip `color` names (ROLE_COLOR) to the shared STATUS_COLORS tone keys.
// "secondary" (CLIENT role here) has no exact equivalent among the 7 shared
// tones — approximated as "neutral", same documented trade-off as every
// other page that used this mapping.
const ROLE_COLOR_TO_TONE = {
  error: "danger",
  info: "info",
  primary: "info",
  default: "neutral",
  secondary: "neutral",
};

const roleLabels = {
  ADMIN: "Administrator",
  MANAGER: "HR Manager",
  RECRUITER: "Recruiter",
  STAFF: "Staff",
  CLIENT: "Client",
};

const ROLE_COLOR = {
  ADMIN: "error",
  MANAGER: "info",
  RECRUITER: "primary",
  STAFF: "default",
  CLIENT: "secondary",
};

// Roles an admin can assign via the inline dropdown
const ASSIGNABLE_ROLES = ["ADMIN", "MANAGER", "RECRUITER", "STAFF", "CLIENT"];

function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

function initialsOf(name) {
  return (name || "U")
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export default function Users() {
  const toast = useToast();
  const navigate = useNavigate();
  const fullAccess = hasFullAccess();
  const admin = isAdmin();
  const me = getUser() || {};

  const [tab, setTab] = useState(0);
  const [active, setActive] = useState([]);
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [roleBusyId, setRoleBusyId] = useState(null);

  // ── Phase 12 (Design Gap Audit item L): visual-parity additions ──────────
  // Search box (client-side filter, no API change).
  const [userSearch, setUserSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState(""); // real, existing role data — client-side only

  // Real per-user "Open roles" / "Submissions (30d)" counts, reusing the
  // EXISTING /jobs and /candidates endpoints (the same data + computation
  // pattern already proven on Dashboard.jsx's recruiter leaderboard) — no
  // new backend endpoint. "Last active" from the prototype is NOT added:
  // AppUser has no last-login/last-active field at all, so it would have
  // to be fabricated.
  const [jobs, setJobs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  useEffect(() => {
    if (!fullAccess) return;
    API.get("/jobs")
      .then((r) => setJobs(r.data || []))
      .catch(() => setJobs([]));
    API.get("/candidates")
      .then((r) => setCandidates(r.data || []))
      .catch(() => setCandidates([]));
  }, [fullAccess]);

  const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
  const openRolesFor = (u) =>
    jobs.filter(
      (j) =>
        (j.assignedRecruiter || "").includes(u.name) &&
        (j.status || "ACTIVE") !== "CLOSED",
    ).length;
  const submissions30dFor = (u) =>
    candidates.filter((c) => {
      if (c.recruiterName !== u.name || !c.candidateDate) return false;
      return Date.now() - new Date(c.candidateDate).getTime() <= THIRTY_DAYS_MS;
    }).length;
  // These counts are only meaningful for recruiting roles.
  const showsWorkload = (u) => u.role === "RECRUITER" || u.role === "MANAGER";

  // Role-edit-behind-a-confirm (Design Gap Audit annotation: "role is no
  // longer an always-live dropdown you can mis-click. It reads as a chip
  // and edits behind a confirm.") — replaces the old always-editable inline
  // <Select>. Uses the exact same onChangeRole API call/business rule
  // below; only the interaction changed, not the underlying logic.
  const [roleEditUser, setRoleEditUser] = useState(null); // user being offered a role change
  const [pendingRole, setPendingRole] = useState(null); // { user, newRole } awaiting confirm

  const loadActive = () =>
    API.get("/account/users").then((r) => setActive(r.data));
  const loadDeleted = () =>
    API.get("/account/users/deleted").then((r) => setDeletedUsers(r.data));

  useEffect(() => {
    if (!fullAccess) {
      setLoading(false);
      return;
    }
    Promise.all([loadActive(), loadDeleted()])
      .catch(() => toast.error("Could not load users"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullAccess]);

  const onChangeRole = async (u, newRole) => {
    if (newRole === u.role) return;
    const isSelf =
      u.email && u.email.toLowerCase() === (me.email || "").toLowerCase();
    if (isSelf) {
      toast.error("You cannot change your own role");
      return;
    }
    setRoleBusyId(u.id);
    try {
      await API.put(`/account/users/${u.id}/role`, { role: newRole });
      toast.success(
        `${u.name || u.email} is now ${roleLabels[newRole] || newRole}`,
      );
      await loadActive();
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not change role"));
    } finally {
      setRoleBusyId(null);
      setPendingRole(null);
      setRoleEditUser(null);
    }
  };

  const onDelete = async (u) => {
    if (u.email && u.email.toLowerCase() === (me.email || "").toLowerCase()) {
      toast.error("You cannot delete your own account");
      return;
    }
    if (
      !window.confirm(
        `Delete ${u.name || u.email}? Their details will be moved to Deleted Users.`,
      )
    )
      return;
    setBusyId(u.id);
    try {
      await API.delete(`/account/users/${u.id}`);
      toast.success(`${u.name || u.email} deleted`);
      await Promise.all([loadActive(), loadDeleted()]);
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not delete user"));
    } finally {
      setBusyId(null);
    }
  };

  if (!fullAccess) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Paper sx={{ textAlign: "center", py: 7 }}>
          <PeopleAltOutlinedIcon sx={{ fontSize: 40, color: ACCENT, mb: 1 }} />
          <Typography sx={{ fontWeight: 700 }}>Access restricted</Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 13, mt: 0.5 }}>
            Only Administrators and HR Managers can view this page.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const rows = (tab === 0 ? active : deletedUsers).filter((u) => {
    if (roleFilter && u.role !== roleFilter) return false;
    const q = userSearch.toLowerCase();
    if (!q) return true;
    return (
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q)
    );
  });

  // Render the Role cell: reads as a chip; ADMIN can click it to reveal the
  // picker, and choosing a role opens a confirm dialog (see ConfirmDialog
  // render below) rather than saving immediately.
  const roleCell = (u) => {
    const isSelf =
      u.email && u.email.toLowerCase() === (me.email || "").toLowerCase();
    const canEdit = admin && tab === 0 && !isSelf;

    if (canEdit && roleEditUser === u.id) {
      return (
        <Select
          size="small"
          autoFocus
          open
          value={u.role}
          disabled={roleBusyId === u.id}
          onClose={() => setRoleEditUser(null)}
          onChange={(e) => {
            if (e.target.value !== u.role)
              setPendingRole({ user: u, newRole: e.target.value });
            setRoleEditUser(null);
          }}
          sx={{ fontSize: 13, minWidth: 140 }}
        >
          {ASSIGNABLE_ROLES.map((r) => (
            <MenuItem key={r} value={r} sx={{ fontSize: 13 }}>
              {roleLabels[r]}
            </MenuItem>
          ))}
        </Select>
      );
    }

    return (
      <StatusBadge
        label={roleLabels[u.role] || u.role}
        tone={ROLE_COLOR_TO_TONE[ROLE_COLOR[u.role]] || "neutral"}
        onClick={canEdit ? () => setRoleEditUser(u.id) : undefined}
      />
    );
  };

  return (
    <PageContainer sx={{ px: { xs: 2, md: 2.5 }, py: { xs: 2, md: 2 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ sm: "center" }}
        spacing={1}
        sx={{ mb: 2, pl: { xs: 5, md: 0 } }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: NAVY }}>
            Users
          </Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 13, mt: 0.25 }}>
            {active.length} active · {deletedUsers.length} deactivated
          </Typography>
        </Box>
        {admin && (
          <Button
            variant="contained"
            onClick={() => navigate("/settings")}
            sx={{ flexShrink: 0 }}
          >
            + New user
          </Button>
        )}
      </Stack>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ px: 2, borderBottom: "1px solid", borderColor: "divider" }}
        >
          <Tab
            icon={<PeopleAltOutlinedIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={`Active (${active.length})`}
            sx={{ minHeight: 56, textTransform: "none", fontWeight: 600 }}
          />
          <Tab
            icon={<PersonOffOutlinedIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={`Deactivated (${deletedUsers.length})`}
            sx={{ minHeight: 56, textTransform: "none", fontWeight: 600 }}
          />
        </Tabs>
      </Paper>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ mb: 2 }}
        alignItems={{ sm: "center" }}
      >
        <Box sx={{ maxWidth: 340, width: "100%" }}>
          <SearchInput
            value={userSearch}
            onChange={setUserSearch}
            placeholder="Search name or email"
          />
        </Box>
        {/* Real Role filter — reuses existing role data, same FilterChipBar
            component already used on Candidates' status filter. */}
        <FilterChipBar
          options={ASSIGNABLE_ROLES.map((r) => ({
            key: r,
            label: roleLabels[r],
          }))}
          selected={roleFilter ? [roleFilter] : []}
          onToggle={(key) => setRoleFilter(roleFilter === key ? "" : key)}
        />
      </Stack>

      {loading ? (
        <Paper sx={{ p: 2 }}>
          <LoadingState variant="rows" rows={4} columns={5} />
        </Paper>
      ) : rows.length === 0 ? (
        <Paper sx={{ py: 2 }}>
          <EmptyState
            icon={
              tab === 0 ? (
                <PeopleAltOutlinedIcon sx={{ fontSize: 28, color: ACCENT }} />
              ) : (
                <PersonOffOutlinedIcon sx={{ fontSize: 28, color: ACCENT }} />
              )
            }
            title={tab === 0 ? "No active users" : "No deactivated users"}
            description={
              userSearch
                ? "Try a different search."
                : tab === 0
                  ? "Create accounts from Settings → Create a new user."
                  : "Users you delete will appear here with their details preserved."
            }
          />
        </Paper>
      ) : (
        <>
          {/* Desktop table */}
          <Paper
            sx={{ overflow: "hidden", display: { xs: "none", md: "block" } }}
          >
            <DataTable
              columns={[
                { key: "user", label: "User" },
                { key: "role", label: "Role" },
                ...(tab === 0
                  ? [
                      { key: "openRoles", label: "Open roles" },
                      { key: "submissions", label: "Submissions (30d)" },
                    ]
                  : []),
                { key: "date", label: tab === 0 ? "Added" : "Deactivated" },
                ...(tab === 0
                  ? [{ key: "actions", label: "Actions", align: "right" }]
                  : []),
              ]}
              rows={rows}
              getRowKey={(u) => u.id}
              renderRow={(u) => {
                const isSelf =
                  u.email &&
                  u.email.toLowerCase() === (me.email || "").toLowerCase();
                return (
                  <TableRow key={u.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          sx={{
                            width: 34,
                            height: 34,
                            fontSize: 13,
                            fontWeight: 700,
                            bgcolor: tab === 0 ? NAVY : "text.disabled",
                          }}
                        >
                          {initialsOf(u.name)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                            {u.name || "—"}
                            {isSelf && (
                              <Typography
                                component="span"
                                sx={{
                                  ml: 1,
                                  fontSize: 11,
                                  color: "text.secondary",
                                  fontWeight: 500,
                                }}
                              >
                                (you)
                              </Typography>
                            )}
                          </Typography>
                          <Typography
                            sx={{ fontSize: 12, color: "text.secondary" }}
                          >
                            {u.email || "—"}
                            {u.phone ? ` · ${u.phone}` : ""}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>{roleCell(u)}</TableCell>
                    {tab === 0 && (
                      <>
                        <TableCell sx={{ fontSize: 13 }}>
                          {showsWorkload(u) ? openRolesFor(u) : "—"}
                        </TableCell>
                        <TableCell sx={{ fontSize: 13 }}>
                          {showsWorkload(u) ? submissions30dFor(u) : "—"}
                        </TableCell>
                      </>
                    )}
                    <TableCell sx={{ fontSize: 13 }}>
                      {fmtDate(tab === 0 ? u.createdAt : u.deletedAt)}
                    </TableCell>
                    {tab === 0 && (
                      <TableCell align="right">
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          startIcon={
                            <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                          }
                          disabled={isSelf || busyId === u.id}
                          onClick={() => onDelete(u)}
                        >
                          {busyId === u.id ? "Deleting…" : "Delete"}
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              }}
            />
          </Paper>

          {/* Mobile cards */}
          <Stack spacing={1.5} sx={{ display: { xs: "flex", md: "none" } }}>
            {rows.map((u) => {
              const isSelf =
                u.email &&
                u.email.toLowerCase() === (me.email || "").toLowerCase();
              return (
                <Paper key={u.id} sx={{ p: 2 }}>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ mb: 1.25 }}
                  >
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        fontWeight: 700,
                        bgcolor: tab === 0 ? NAVY : "text.disabled",
                      }}
                    >
                      {initialsOf(u.name)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
                        {u.name || "—"} {isSelf && "(you)"}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 12.5,
                          color: "text.secondary",
                          wordBreak: "break-all",
                        }}
                      >
                        {u.email}
                      </Typography>
                    </Box>
                    {tab === 0 ? (
                      roleCell(u)
                    ) : (
                      <StatusBadge
                        label={roleLabels[u.role] || u.role}
                        tone={
                          ROLE_COLOR_TO_TONE[ROLE_COLOR[u.role]] || "neutral"
                        }
                      />
                    )}
                  </Stack>

                  {u.phone && (
                    <Typography
                      sx={{ fontSize: 12.5, color: "text.secondary", mb: 0.5 }}
                    >
                      📞 {u.phone}
                    </Typography>
                  )}

                  <Typography
                    sx={{ fontSize: 12, color: "text.secondary", mb: 1 }}
                  >
                    {tab === 0
                      ? `Added: ${fmtDate(u.createdAt)}`
                      : `Deactivated: ${fmtDate(u.deletedAt)}`}
                  </Typography>

                  {tab === 0 && !isSelf && (
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      fullWidth
                      startIcon={<DeleteOutlineIcon sx={{ fontSize: 15 }} />}
                      disabled={busyId === u.id}
                      onClick={() => onDelete(u)}
                    >
                      {busyId === u.id ? "Deleting…" : "Delete"}
                    </Button>
                  )}
                </Paper>
              );
            })}
          </Stack>
        </>
      )}

      <ConfirmDialog
        open={!!pendingRole}
        title="Change role?"
        message={
          pendingRole
            ? `Change ${pendingRole.user.name || pendingRole.user.email}'s role from ${roleLabels[pendingRole.user.role]} to ${roleLabels[pendingRole.newRole]}?`
            : ""
        }
        confirmLabel="Change role"
        loading={pendingRole ? roleBusyId === pendingRole.user.id : false}
        onConfirm={() =>
          pendingRole && onChangeRole(pendingRole.user, pendingRole.newRole)
        }
        onClose={() => setPendingRole(null)}
      />
    </PageContainer>
  );
}
